import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { RefreshCw, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function JobsMonitorPage() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/jobs').then((r) => { setJobs(r.data.items); setTotal(r.data.total); setLoading(false); });

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleRetry = async (id) => {
    try {
      await api.post(`/jobs/${id}/retry`);
      toast.success('Job queued for retry');
      load();
    } catch { toast.error('Retry failed'); }
  };

  const statusStyle = {
    pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    processing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    done: 'bg-green-500/10 text-green-400 border-green-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Loading jobs...</div>;

  return (
    <div data-testid="jobs-monitor-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">İşler</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">{total} toplam iş</p>
        </div>
        <Button variant="outline" onClick={load} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5" data-testid="refresh-jobs-btn">
          <RefreshCw size={16} className="mr-2" /> Yenile
        </Button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-20">
          <Activity size={40} className="mx-auto text-[#262626] mb-4" />
          <p className="text-[#737373] font-ibm text-sm">Henüz iş yok</p>
        </div>
      ) : (
        <Card className="bg-[#141414] border-[#262626]">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent">
                <TableHead className="text-[#A3A3A3] font-ibm">İş ID</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm">Tür</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm">Durum</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm">İlerleme</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm">Tekrar</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm">Oluşturulma</TableHead>
                <TableHead className="text-[#A3A3A3] font-ibm text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} className="border-[#262626] hover:bg-white/5 transition-colors duration-200" data-testid={`job-row-${job.id}`}>
                  <TableCell className="font-jetbrains text-xs text-[#A3A3A3]">{job.id.slice(0, 8)}...</TableCell>
                  <TableCell className="capitalize font-ibm text-white">{job.payload?.content_type?.replace('_', ' ') || job.type}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={statusStyle[job.status] || ''}>{job.status}</Badge>
                  </TableCell>
                  <TableCell className="w-32">
                    <Progress value={job.progress || 0} className="h-1.5" />
                  </TableCell>
                  <TableCell className="text-[#737373] font-jetbrains text-xs">{job.retry_count || 0}</TableCell>
                  <TableCell className="text-[#737373] font-jetbrains text-xs">{new Date(job.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {job.status === 'failed' && (
                      <Button variant="ghost" size="sm" onClick={() => handleRetry(job.id)} className="text-[#A3A3A3] hover:text-white h-8" data-testid={`retry-job-${job.id}`}>
                        <RefreshCw size={14} className="mr-1" /> Tekrar Dene
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
